import i18n from "i18next";
import {
	PartConfigurationDocumentValue,
	PartConfigurationPropertyEvaluation,
	PartConfigurationSchemaOption,
	PartConfigurationSchemaProperty,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";

export function getPropertyModelDocumentValue(value: unknown): PartConfigurationDocumentValue | undefined {
	if (typeof value !== "object" || value == null) return undefined;
	if (!("value" in value) || typeof value.value !== "string") return undefined;
	if (!("fileName" in value) || typeof value.fileName !== "string") return undefined;
	if (!("documentDescription" in value) || typeof value.documentDescription !== "string") return undefined;
	if (!("documentCategoryId" in value) || typeof value.documentCategoryId !== "number") return undefined;
	return {
		value: value.value,
		fileName: value.fileName,
		documentDescription: value.documentDescription,
		documentCategoryId: value.documentCategoryId,
	};
}

/**
 * The options to choose from: those that the rules allow, and the current value even if they do not, so that the
 * field shows it with its issue. A free value that is not an option is shown as its own option.
 */
export function getPropertyModelSelectableOptions({
	property,
	evaluation,
}: {
	property: PartConfigurationSchemaProperty;
	evaluation: PartConfigurationPropertyEvaluation;
}): PartConfigurationSchemaOption[] {
	const allowedKeys = evaluation.allowedOptionKeys == null ? null : new Set(evaluation.allowedOptionKeys);
	const currentValue = typeof evaluation.value === "string" ? evaluation.value : null;
	const options = (property.options ?? []).filter(
		(option) => allowedKeys == null || allowedKeys.has(option.key) || option.key === currentValue,
	);
	if (currentValue != null && !options.some((option) => option.key === currentValue)) {
		options.push({ key: currentValue, label: currentValue, attributes: {} });
	}
	return options;
}

/** Tells that a rule fixes the value and the range that the rules allow for a number. */
export function getPropertyModelFieldHelperText(evaluation: PartConfigurationPropertyEvaluation): string | undefined {
	const parts: string[] = [];
	if (evaluation.fixed) parts.push(i18n.t("value_fixed_by_rule"));
	if (evaluation.minValue != null && evaluation.maxValue != null) {
		parts.push(`${evaluation.minValue}–${evaluation.maxValue}`);
	} else if (evaluation.minValue != null) {
		parts.push(`≥ ${evaluation.minValue}`);
	} else if (evaluation.maxValue != null) {
		parts.push(`≤ ${evaluation.maxValue}`);
	}
	return parts.length > 0 ? parts.join(", ") : undefined;
}
